// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { CalendarIcon } from "../../assets/svgs/SVGs";

/**
 * The app's date picker (MUI DesktopDatePicker, white on dark).
 *
 * `variant`:
 *   - "default": 3rem tall, 8px corners (forms in pages and modals).
 *   - "rounded": input-height, 10px corners (filters, accordions, media).
 *
 * Partial typing never reaches the caller: onChange only fires with a valid
 * date or null, while the field keeps showing what is being typed.
 */

const isSameValue = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (a.isValid && b.isValid) {
    if (a.isValid() && b.isValid()) return a.isSame(b);
    return a.isValid() === b.isValid();
  }
  return false;
};

/** Mirrors the controlled `value` while letting the field hold partial input. */
const useInnerValue = (value, defaultValue) => {
  const [innerValue, setInnerValue] = useState(value !== undefined ? value : defaultValue);
  const lastValue = useRef(value);

  useEffect(() => {
    if (isSameValue(value, lastValue.current)) return;
    lastValue.current = value;
    setInnerValue((prev) => (isSameValue(value, prev) ? prev : value));
  }, [value]);

  return [innerValue, setInnerValue];
};

const paperSx = {
  "& .MuiPickersYear-yearButton": { color: "black" },
  "& .MuiPickersYear-yearButton.Mui-disabled": { color: "gray" },
  "& .MuiYearCalendar-button": { color: "black" },
  "& .MuiYearCalendar-button.Mui-disabled": { color: "gray" },
  "& .MuiYearCalendar-button.Mui-selected": { color: "white" },
  "& .MuiPickersDay-root.Mui-disabled": { color: "gray" },
};

const DatePickerBase = (props) => {
  const { slots, slotProps, onChange, value, defaultValue, format, ...rest } = props;
  const [innerValue, setInnerValue] = useInnerValue(value, defaultValue);

  return (
    <DesktopDatePicker
      value={value !== undefined ? (innerValue !== undefined ? innerValue : null) : undefined}
      defaultValue={defaultValue}
      onChange={(newValue, context) => {
        if (value !== undefined) setInnerValue(newValue);
        if (newValue === null || newValue?.isValid?.()) onChange?.(newValue, context);
      }}
      slotProps={{
        ...(slotProps || {}),
        desktopPaper: {
          ...(slotProps?.desktopPaper || {}),
          sx: { ...paperSx, ...(slotProps?.desktopPaper?.sx || {}) },
        },
      }}
      slots={{ openPickerIcon: CalendarIcon, ...(slots || {}) }}
      format={format || "MM/DD/YYYY"}
      {...rest}
    />
  );
};

const sharedStyles = {
  color: "#fff",
  width: "100%",
  "& span": { color: "#fff !important" },
  "& .MuiInputAdornment-positionEnd button svg": { height: "25px" },
  "& .MuiYearCalendar-button": { color: "black !important" },
};

const variantStyles = {
  default: {
    height: "3rem",
    "& fieldset": { borderColor: "white !important", borderRadius: "0.5rem" },
    // MUI v6+ renders MuiPickersSectionList instead of an <input>: cover both.
    "& input": { color: "#fff !important" },
    "& .MuiPickersSectionList-root": {
      color: "#fff !important",
      paddingTop: "0.85rem !important",
      paddingBottom: "0.85rem !important",
    },
    "& .MuiPickersInputBase-root, & .MuiPickersInputBase-sectionsContainer": {
      color: "#fff !important",
    },
    "& .MuiPickersSectionList-section, & .MuiPickersSectionList-sectionContent": {
      color: "#fff !important",
    },
  },
  rounded: {
    "& input": { color: "white", paddingBlock: "13.5px" },
    "& fieldset": { borderColor: "white", borderRadius: "10px" },
  },
};

export const CustomDatePicker = styled(DatePickerBase, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ variant = "default" }) => ({
  ...sharedStyles,
  ...(variantStyles[variant] || variantStyles.default),
}));
